import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Checkbox,
    Tab,
    Tabs,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Link,
    CircularProgress,
    TableSortLabel,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    useTheme,
    Typography,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { RenderParticipantName } from './Elements';
import {
    fetchContestGroupings2,
    fetchContestRankings,
    fetchContestMetadata,
    getContestRankingOrder,
    RankingEntry,
    ContestField,
    ContestGrouping,
} from '../api/contests';
import { useLoadingCounter } from '../utils/useLoadingCounter';
import { SwipeHint } from '../utils/SwipeHint';
import Medal from '../utils/Medal';
import { Bar, Line } from 'react-chartjs-2';
import useStyles from './useStyles';
import { useParticipantRanks } from '../utils/useParticipantRanks';
import { formatTimeValueToClock } from '../utils/timeResult';
import SearchBar from './SearchBar';

import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend, LineElement, PointElement);

type RecordPoint = {
    grouping: string;
    date: string; // ISO
    participant_id: number;
    participant_name: string;
    result: number;
    result_unit?: string;
    evidence_url?: string;
};

type RecordsCacheEntry = {
    createdAtMs: number;
    data: Map<string, RecordPoint[]>;
};

type RankingOrder = 'asc' | 'desc';

type RecordsSortField = 'date' | 'result' | 'participant';

const POPULAR_GROUP_MIN_COUNT = 15;
const RECORDS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

const RankingView: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { contestId } = useParams<{ contestId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { increment, decrement, isLoading } = useLoadingCounter();
    const classes = useStyles();

    const [groupData, setGroupData] = useState<ContestGrouping[]>([]);
    const [selectedGrouping, setSelectedGrouping] = useState<string | null>(null);
    const [rankingEntries, setRankingEntries] = useState<RankingEntry[]>([]);
    const [fields, setFields] = useState<ContestField[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [showBestPerParticipant, setShowBestPerParticipant] = useState(false);
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [open, setOpen] = useState(false);
    const [participantData, setParticipantData] = useState<string>('');
    const [chartOpen, setChartOpen] = useState(false);
    const [recordsOpen, setRecordsOpen] = useState(false);
    const [recordsByGroup, setRecordsByGroup] = useState<Map<string, RecordPoint[]>>(new Map());
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [recordsError, setRecordsError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Needed for correct "record progression" logic:
    // - 'desc' => higher is better (reps)
    // - 'asc'  => lower is better (time)
    const [contestRankingOrder, setContestRankingOrder] = useState<RankingOrder>('desc');

    // Sorting inside "records chronicle" tables
    const [recordsSortField, setRecordsSortField] = useState<RecordsSortField>('date');
    const [recordsSortDirection, setRecordsSortDirection] = useState<'asc' | 'desc'>('asc');

    // Cache: contestId -> (minCount -> RecordsCacheEntry)
    const recordsCacheRef = useRef<Map<string, Map<number, RecordsCacheEntry>>>(new Map());

    const handleRecordsSort = useCallback(
        (field: RecordsSortField) => {
            const isAsc = recordsSortField === field && recordsSortDirection === 'asc';
            setRecordsSortField(field);
            setRecordsSortDirection(isAsc ? 'desc' : 'asc');
        },
        [recordsSortField, recordsSortDirection]
    );

    const sortHistory = useCallback(
        (history: RecordPoint[]): RecordPoint[] => {
            const dir = recordsSortDirection === 'asc' ? 1 : -1;
            return [...history].sort((a, b) => {
                if (recordsSortField === 'date') {
                    const ta = new Date(a.date).getTime();
                    const tb = new Date(b.date).getTime();
                    return (ta - tb) * dir;
                }
                if (recordsSortField === 'result') {
                    return (a.result - b.result) * dir;
                }
                // participant
                return (
                    String(a.participant_name ?? '').localeCompare(String(b.participant_name ?? ''), 'ru') *
                    dir
                );
            });
        },
        [recordsSortField, recordsSortDirection]
    );

    const initialGrouping = useMemo(
        () => new URLSearchParams(location.search).get('group') || 'general',
        [location.search]
    );

    const compareGroupingsByName = useCallback((a: ContestGrouping, b: ContestGrouping) => {
        // Sort ONLY by grouping name (string), descending. No numeric parsing.
        return String(b.grouping ?? '').localeCompare(String(a.grouping ?? ''), 'ru');
    }, []);

    // Sort categories (groupings) by name for consistent tab order.
    // IMPORTANT: don't mutate state objects (Array.sort mutates), so always sort a copy.
    const sortedGroupData = useMemo(
        () => (groupData ? [...groupData].sort(compareGroupingsByName) : []),
        [groupData, compareGroupingsByName]
    );
    const groupings = useMemo(() => sortedGroupData.map((g) => g.grouping), [sortedGroupData]);

    const mainResultField = useMemo(
        () => fields.find((f) => f.main_result_field)?.field_name || 'result',
        [fields]
    );
    const mainResultLabel = useMemo(
        () => fields.find((f) => f.main_result_field)?.display_label || 'Результат',
        [fields]
    );
    const tableFields = useMemo(
        () => fields.filter((f) => f.field_name !== mainResultField),
        [fields, mainResultField]
    );

    const isTimeResult = useMemo(() => {
        const mainField: any = fields.find((f) => f.main_result_field) || fields.find((f) => f.result_field);
        return String(mainField?.data_type ?? '').toLowerCase() === 'time';
    }, [fields]);

    const { ranksByParticipantId } = useParticipantRanks();

    useEffect(() => {
        if (contestId) {
            // ranking_order is needed for correct record chronicle (asc => time: lower is better)
            increment();
            getContestRankingOrder(contestId)
                .then((order) => setContestRankingOrder(order))
                .catch((_) => setContestRankingOrder('desc'))
                .finally(decrement);

            increment();
            fetchContestGroupings2(contestId)
                .then((data) => {
                    setGroupData(data);
                    const groups = (data ? [...data] : []).sort(compareGroupingsByName).map((d) => d.grouping);
                    if (!groups?.includes(initialGrouping)) {
                        setSelectedGrouping(groups[0] || 'general');
                    } else {
                        setSelectedGrouping(initialGrouping);
                    }
                })
                .catch((_) => setError('Ошибка при получении группировок'))
                .finally(decrement);

            increment();
            fetchContestMetadata(contestId)
                .then((data) => setFields(data))
                .catch((_) => setError('Ошибка при получении метаданных'))
                .finally(decrement);
        }
    }, [contestId, initialGrouping, increment, decrement, compareGroupingsByName]);

    useEffect(() => {
        if (selectedGrouping && contestId) {
            increment();
            fetchContestRankings(contestId, selectedGrouping)
                .then((data) => setRankingEntries(data))
                .catch((_) => setError('Ошибка при получении рейтингов'))
                .finally(decrement);
        }
    }, [selectedGrouping, contestId, increment, decrement]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const group = params.get('group');
        if (group && group !== selectedGrouping) {
            setSelectedGrouping(group);
        }
    }, [location.search, selectedGrouping]);

    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        const newGrouping = groupings[newValue];
        setSelectedGrouping(newGrouping);
        const params = new URLSearchParams(location.search);
        params.set('group', newGrouping);
        navigate({ search: params.toString() }, { replace: true });
    }, [groupings, location.search, navigate]);

    const handleSort = useCallback((field: string) => {
        const isAsc = sortField === field && sortDirection === 'asc';
        setSortField(field);
        setSortDirection(isAsc ? 'desc' : 'asc');
    }, [sortField, sortDirection]);

    const searchedEntries = useMemo(() => {
        const normalizedSearch = searchText.toLowerCase();
        return rankingEntries.filter((entry) =>
            entry.participant_name?.toLowerCase().includes(normalizedSearch)
        );
    }, [rankingEntries, searchText]);

    const bestFilteredEntries = useMemo(() => {
        if (!showBestPerParticipant) {
            return searchedEntries;
        }

        const getComparableResult = (entry: RankingEntry): number => {
            const mainValue = Number(entry[mainResultField]);
            if (!Number.isNaN(mainValue)) {
                return mainValue;
            }

            const fallbackValue = Number(entry.result);
            if (!Number.isNaN(fallbackValue)) {
                return fallbackValue;
            }

            return contestRankingOrder === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
        };

        const bestByParticipant = new Map<number, RankingEntry>();

        for (const entry of searchedEntries) {
            const existing = bestByParticipant.get(entry.participant_id);
            if (!existing) {
                bestByParticipant.set(entry.participant_id, entry);
                continue;
            }

            const entryResult = getComparableResult(entry);
            const existingResult = getComparableResult(existing);
            const isBetter = contestRankingOrder === 'asc'
                ? entryResult < existingResult
                : entryResult > existingResult;

            if (isBetter) {
                bestByParticipant.set(entry.participant_id, entry);
                continue;
            }

            if (entryResult !== existingResult) {
                continue;
            }

            const entryRanking = Number(entry.ranking ?? Number.POSITIVE_INFINITY);
            const existingRanking = Number(existing.ranking ?? Number.POSITIVE_INFINITY);
            if (entryRanking < existingRanking) {
                bestByParticipant.set(entry.participant_id, entry);
                continue;
            }

            if (entryRanking !== existingRanking) {
                continue;
            }

            const entryCreatedAt = entry.created_at ? new Date(entry.created_at).getTime() : 0;
            const existingCreatedAt = existing.created_at ? new Date(existing.created_at).getTime() : 0;
            if (entryCreatedAt > existingCreatedAt) {
                bestByParticipant.set(entry.participant_id, entry);
            }
        }

        return searchedEntries.filter((entry) => bestByParticipant.get(entry.participant_id) === entry);
    }, [searchedEntries, showBestPerParticipant, contestRankingOrder, mainResultField]);

    const sortedEntries = useMemo(() => {
        return [...bestFilteredEntries].sort((a, b) => {
            if (!sortField) return 0;
            const aValue = a[sortField];
            const bValue = b[sortField];
            if (aValue === bValue) return 0;
            return (aValue < bValue ? -1 : 1) * (sortDirection === 'asc' ? 1 : -1);
        });
    }, [bestFilteredEntries, sortField, sortDirection]);

    const isDarkMode = theme.palette.mode === 'dark';
    const chartColors = {
        background: isDarkMode ? '#4caf50' : '#1976d2',
        text: isDarkMode ? '#ffffff' : '#000000',
        grid: isDarkMode ? '#666666' : '#cccccc',
    };

    const handleChartOpen = () => setChartOpen(true);
    const handleChartClose = () => setChartOpen(false);
    const handleRecordsOpen = () => setRecordsOpen(true);
    const handleRecordsClose = () => setRecordsOpen(false);

    const chartData = {
        labels: sortedGroupData.map((group) => group.grouping),
        datasets: [
            {
                label: 'Публикаций',
                data: sortedGroupData.map((group) => group.group_count),
                backgroundColor: chartColors.background,
            },
        ],
    };

    const chartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.raw} публ.`,
                },
            },
        },
        scales: {
            x: {
                title: { display: true, text: isMobile ? 'Публикаций' : 'Группы', color: chartColors.text },
                ticks: { color: chartColors.text },
                grid: { color: chartColors.grid },
                beginAtZero: true
            },
            y: {
                title: { display: true, text: isMobile ? 'Группы' : 'Публикаций' },
                ticks: { color: chartColors.text },
                grid: { color: chartColors.grid },
                beginAtZero: true },
        },
        indexAxis: isMobile ? 'y' : 'x',
    };

    const handleClearSearch = () => setSearchText('');
    const handleOpenParticipantData = (data: string) => {
        setParticipantData(data);
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    const popularGroups = useMemo(() => {
        return sortedGroupData
            .filter((g) => (g.group_count ?? 0) >= POPULAR_GROUP_MIN_COUNT)
            .slice()
            .sort((a, b) => {
                const ca = Number(a.group_count ?? 0);
                const cb = Number(b.group_count ?? 0);
                if (cb !== ca) return cb - ca; // higher first
                // stable-ish fallback: keep name sorting for equal counts
                return String(b.grouping ?? '').localeCompare(String(a.grouping ?? ''), 'ru');
            });
    }, [sortedGroupData]);

    // Build record progression (world record history) for one group.
    // NOTE: We assume 'higher is better' for now (desc). If in future you want asc-contests,
    // we can wire contest ranking_order into client metadata.
    const buildRecordHistory = useCallback((entries: RankingEntry[]): RecordPoint[] => {
        const withDate = entries
            .filter((e) => !!e.created_at)
            .map((e) => ({
                ...e,
                _t: new Date(e.created_at as string).getTime(),
            }))
            .filter((e) => !Number.isNaN(e._t))
            .sort((a, b) => a._t - b._t);

        const history: RecordPoint[] = [];
        let best: number | null = null;

        for (const e of withDate) {
            const val = Number(e.result);
            const isBetter = best === null
                ? true
                : (contestRankingOrder === 'asc' ? val < best : val > best);

            if (isBetter) {
                best = val;
                history.push({
                    grouping: String(e.grouping ?? ''),
                    date: new Date(e._t).toISOString(),
                    participant_id: e.participant_id,
                    participant_name: e.participant_name,
                    result: val,
                    result_unit: e.result_unit,
                    evidence_url: e.evidence_url,
                });
            }
        }
        return history;
    }, [contestRankingOrder]);

    const formatRecordResult = useCallback((val: number, unit?: string) => {
        if (isTimeResult) {
            return formatTimeValueToClock(val);
        }
        return `${val}${unit ? ` ${unit}` : ''}`;
    }, [isTimeResult]);

    // Invalidate records cache if groupData changes (new submissions likely happened)
    useEffect(() => {
        if (!contestId) return;
        // Conservative strategy: if groupData updates, drop cached records for this contest.
        recordsCacheRef.current.delete(contestId);
    }, [contestId, groupData]);

    useEffect(() => {
        if (!recordsOpen) return;
        if (!contestId) return;

        // Cache lookup
        const contestCache = recordsCacheRef.current.get(contestId) ?? new Map<number, RecordsCacheEntry>();
        if (!recordsCacheRef.current.has(contestId)) recordsCacheRef.current.set(contestId, contestCache);

        // Stable numeric key derived from threshold + ranking order.
        // This avoids mixing "time" (asc) vs "reps" (desc) logic in cache.
        const numericKey = contestRankingOrder === 'asc'
            ? POPULAR_GROUP_MIN_COUNT * 10 + 1
            : POPULAR_GROUP_MIN_COUNT * 10 + 2;

        const cached = contestCache.get(numericKey);
        const now = Date.now();
        if (cached && now - cached.createdAtMs < RECORDS_CACHE_TTL_MS) {
            setRecordsByGroup(cached.data);
            setRecordsLoading(false);
            setRecordsError(null);
            return;
        }

        // load record history for popular groups on demand
        let cancelled = false;
        setRecordsLoading(true);
        setRecordsError(null);

        (async () => {
            try {
                const map = new Map<string, RecordPoint[]>();

                // Limit parallelism a bit to avoid hammering API
                const groups = popularGroups.map((g) => g.grouping).filter(Boolean);
                const batchSize = 4;

                for (let i = 0; i < groups.length; i += batchSize) {
                    const batch = groups.slice(i, i + batchSize);
                    const batchResults = await Promise.all(
                        batch.map(async (grouping) => {
                            const rows = await fetchContestRankings(contestId, grouping);
                            return [grouping, buildRecordHistory(rows)] as const;
                        })
                    );
                    for (const [grouping, history] of batchResults) {
                        map.set(grouping, history);
                    }
                }

                if (!cancelled) {
                    contestCache.set(numericKey, { createdAtMs: Date.now(), data: map });
                    setRecordsByGroup(map);
                }
            } catch (e) {
                if (!cancelled) {
                    setRecordsError('Не удалось построить летопись рекордов');
                }
            } finally {
                if (!cancelled) {
                    setRecordsLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [recordsOpen, contestId, popularGroups, buildRecordHistory, contestRankingOrder]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'background.default', color: 'text.primary' }}>
            <Box display="flex" justifyContent="flex-end" mb={2} gap={1}>
                <IconButton onClick={handleRecordsOpen} color="primary" title="Летопись рекордов (популярные группы)" aria-label="Open records">
                    <TimelineIcon />
                </IconButton>
                <IconButton onClick={handleChartOpen} color="primary" title="График публикаций по группам" aria-label="Open chart">
                    <BarChartIcon />
                </IconButton>
            </Box>

            {groupings.length > 1 && (
                <Tabs
                    value={groupings.indexOf(selectedGrouping ?? '')}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile={true}
                    className={classes.stickyHeader}
                >
                    {groupings.map((grouping) => (
                        <Tab key={grouping} label={grouping} />
                    ))}
                </Tabs>
            )}

            <Box
                sx={{
                    padding: 2,
                    bgcolor: 'background.paper',
                    position: 'sticky',
                    top: 48,
                    zIndex: 9,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <SearchBar
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    onClearSearch={handleClearSearch}
                />
                <FormControlLabel
                    sx={{ mt: 1, mb: 0 }}
                    control={(
                        <Checkbox
                            size="small"
                            checked={showBestPerParticipant}
                            onChange={(event) => setShowBestPerParticipant(event.target.checked)}
                        />
                    )}
                    label="Только лучший результат каждого участника"
                />
            </Box>

            <SwipeHint />
            <Box className={classes.tableContainer}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Участник</TableCell>
                                <TableCell align="left">
                                    <TableSortLabel
                                        active={sortField === 'ranking'}
                                        direction={sortField === 'ranking' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('ranking')}
                                    >
                                        Место
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="left">
                                    <TableSortLabel
                                        active={sortField === mainResultField}
                                        direction={sortField === mainResultField ? sortDirection : 'asc'}
                                        onClick={() => handleSort(mainResultField)}
                                    >
                                        {mainResultLabel}
                                    </TableSortLabel>
                                </TableCell>
                                {tableFields.map((field) => (
                                    <TableCell key={field.field_name} align="left">
                                        <TableSortLabel
                                            active={sortField === field.field_name}
                                            direction={sortField === field.field_name ? sortDirection : 'asc'}
                                            onClick={() => handleSort(field.field_name)}
                                        >
                                            {field.display_label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell>Инфо</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === "created_at"}
                                        direction={sortField === "created_at" ? sortDirection : 'asc'}
                                        onClick={() => handleSort("created_at")}
                                    >
                                        Дата
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Видео</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedEntries.map((entry, index) => (
                                <TableRow key={index}>
                                    <TableCell>{RenderParticipantName(entry.participant_id, entry.participant_name, entry.profile_picture, true, ranksByParticipantId.get(entry.participant_id) ?? [])}</TableCell>
                                    <TableCell align="left"><Medal ranking={entry.ranking} /></TableCell>
                                    <TableCell align="left">{entry[mainResultField]}</TableCell>
                                    {tableFields.map((field) => (
                                        <TableCell key={field.field_name} align="left">{entry[field.field_name]}</TableCell>
                                    ))}
                                    <TableCell>
                                        {entry.participant_data && (
                                            <IconButton onClick={() => handleOpenParticipantData(entry.participant_data)} size="small">
                                                <InfoOutlined fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                    <TableCell align="left">
                                        {entry.created_at ? (
                                            <Typography variant="body2">
                                                {new Date(entry.created_at).toLocaleDateString('ru-RU')}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        {entry.evidence_url && (
                                            <Link href={entry.evidence_url} target="_blank" rel="noopener noreferrer">
                                                Видео
                                            </Link>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            <Dialog open={chartOpen} onClose={handleChartClose} fullWidth maxWidth="md">
                <DialogTitle>Публикаций в группах</DialogTitle>
                <DialogContent>
                    <Box sx={{ height: isMobile ? 500 : 400, display: 'flex', justifyContent: 'center' }}>
                        <Bar data={chartData} options={chartOptions} />
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog open={recordsOpen} onClose={handleRecordsClose} fullWidth maxWidth="md">
                <DialogTitle>Летопись рекордов (популярные группы)</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Показываются группы, где публикаций ≥ {POPULAR_GROUP_MIN_COUNT}. Данные кешируются на клиенте после первой загрузки.
                    </Typography>

                    {recordsLoading ? (
                        <Box display="flex" justifyContent="center" mt={2} mb={2}>
                            <CircularProgress />
                        </Box>
                    ) : recordsError ? (
                        <Typography color="error">{recordsError}</Typography>
                    ) : popularGroups.length === 0 ? (
                        <Typography variant="body2">Нет групп с публикациями ≥ {POPULAR_GROUP_MIN_COUNT}.</Typography>
                    ) : (
                        <Box>
                            {popularGroups.map((g) => {
                                const rawHistory = recordsByGroup.get(g.grouping) ?? [];
                                const history = sortHistory(rawHistory);

                                const lineData = {
                                    labels: rawHistory.map((p) => new Date(p.date).toLocaleDateString('ru-RU')),
                                    datasets: [
                                        {
                                            label: 'Рекорд',
                                            data: rawHistory.map((p) => p.result),
                                            borderColor: theme.palette.primary.main,
                                            backgroundColor: theme.palette.primary.main,
                                            stepped: true,
                                            pointRadius: 2,
                                            tension: 0,
                                        },
                                    ],
                                };

                                const lineOptions: any = {
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            callbacks: {
                                                label: (ctx: any) => {
                                                    const raw = Number(ctx?.raw);
                                                    if (isTimeResult) {
                                                        return `Рекорд: ${formatTimeValueToClock(raw)}`;
                                                    }
                                                    return `Рекорд: ${raw}`;
                                                  }
                                              }
                                          }
                                      },
                                    scales: {
                                        x: { ticks: { display: false } },
                                        y: { beginAtZero: false },
                                    },
                                };

                                return (
                                    <Accordion key={g.grouping} defaultExpanded={false} sx={{ mb: 1 }}>
                                        <AccordionSummary expandIcon={<InfoOutlined />}>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, width: '100%' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {g.grouping}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    публикаций: {g.group_count} • рекордных обновлений: {rawHistory.length}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {rawHistory.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    Нет данных для построения рекордов (возможно, отсутствует created_at).
                                                </Typography>
                                            ) : (
                                                <>
                                                    <Box sx={{ height: 120, mb: 2 }}>
                                                        <Line data={lineData} options={lineOptions} />
                                                    </Box>

                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>
                                                                    <TableSortLabel
                                                                        active={recordsSortField === 'date'}
                                                                        direction={recordsSortField === 'date' ? recordsSortDirection : 'asc'}
                                                                        onClick={() => handleRecordsSort('date')}
                                                                    >
                                                                        Дата
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <TableSortLabel
                                                                        active={recordsSortField === 'participant'}
                                                                        direction={recordsSortField === 'participant' ? recordsSortDirection : 'asc'}
                                                                        onClick={() => handleRecordsSort('participant')}
                                                                    >
                                                                        Участник
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <TableSortLabel
                                                                        active={recordsSortField === 'result'}
                                                                        direction={recordsSortField === 'result' ? recordsSortDirection : 'asc'}
                                                                        onClick={() => handleRecordsSort('result')}
                                                                    >
                                                                        Рекорд
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell>Видео</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {history.map((p, idx) => (
                                                                <TableRow key={`${p.grouping}-${idx}-${p.participant_id}`}>
                                                                    <TableCell>
                                                                        <Typography variant="body2">
                                                                            {new Date(p.date).toLocaleDateString('ru-RU')}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {RenderParticipantName(
                                                                            p.participant_id,
                                                                            p.participant_name,
                                                                            '',
                                                                            true,
                                                                            ranksByParticipantId.get(p.participant_id) ?? []
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                            {formatRecordResult(p.result, p.result_unit)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {p.evidence_url ? (
                                                                            <Link href={p.evidence_url} target="_blank" rel="noopener noreferrer">
                                                                                Видео
                                                                            </Link>
                                                                        ) : (
                                                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Информация участника</DialogTitle>
                <DialogContent>
                    <Typography>{participantData}</Typography>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default RankingView;

